import React from 'react';
import { Eye, FileText, ImageIcon } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/common/utils';
import { PasseriniAPI } from '@/services/apiService';
import { DniPreviewDialog } from '../DniPreviewDialog';
import type { GaranteRecord, GaranteFileRecord } from '../../types';
import type { DniDocumentoRecord } from '../../types';

interface GarantesListSectionProps {
  garantes: GaranteRecord[];
}

interface SelectedGarantePreview {
  documento: DniDocumentoRecord;
  title: string;
}

const IMAGE_MIME_PREFIX = 'image/';

/**
 * Convierte un GaranteFileRecord al shape que espera DniPreviewDialog.
 * Los campos ausentes (id, uploadedBy, createdAt) se rellenan con valores neutros.
 */
function toDocumentoRecord(file: GaranteFileRecord): DniDocumentoRecord {
  return {
    id: 0, // no usado en la vista previa
    fileName: file.fileName,
    fileSize: file.fileSize,
    formattedSize: file.formattedSize,
    mimeType: file.mimeType,
    fileUrl: file.fileUrl,
    uploadedBy: null,
    createdAt: null,
  };
}

export const GarantesListSection: React.FC<GarantesListSectionProps> = ({ garantes }) => {
  const [selectedPreview, setSelectedPreview] = React.useState<SelectedGarantePreview | null>(null);

  if (garantes.length === 0) {
    return (
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Garantes asociados</h4>
        <p className="text-sm text-gray-500">No hay garantes registrados para este trámite.</p>
      </div>
    );
  }

  return (
    <>
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Garantes asociados</h4>
        <ul className="space-y-2 text-sm text-gray-600 max-h-[400px] overflow-y-auto pr-1">
          {garantes.map((garante) => {
            const entries: Array<['frente' | 'dorso', GaranteFileRecord]> = [];
            if (garante.dniFrente) entries.push(['frente', garante.dniFrente]);
            if (garante.dniDorso) entries.push(['dorso', garante.dniDorso]);

            return (
              <li key={garante.id} className="rounded-md border border-gray-100 bg-gray-50 p-3">
                <p className="break-words font-medium text-gray-900">{garante.nombre}</p>

                <div className="mt-3">
                  {entries.length === 0 ? (
                    <p className="text-xs text-gray-500">Sin imágenes de DNI adjuntas.</p>
                  ) : (
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {entries.map(([lado, file]) => (
                        <GaranteThumbnailCard
                          key={`${garante.id}-${lado}`}
                          file={file}
                          lado={lado}
                          garanteNombre={garante.nombre}
                          onOpenPreview={(preview) => setSelectedPreview(preview)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <DniPreviewDialog
        documento={selectedPreview?.documento ?? null}
        open={Boolean(selectedPreview)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedPreview(null);
          }
        }}
        title={selectedPreview?.title ?? 'Previsualización de DNI'}
      />
    </>
  );
};

interface GaranteThumbnailCardProps {
  file: GaranteFileRecord;
  lado: 'frente' | 'dorso';
  garanteNombre: string;
  onOpenPreview: (preview: SelectedGarantePreview) => void;
}

const GaranteThumbnailCard: React.FC<GaranteThumbnailCardProps> = ({
  file,
  lado,
  garanteNombre,
  onOpenPreview,
}) => {
  const { previewUrl, isLoading, hasPreviewError, isImage } = useGaranteThumbnail(file);
  const sideLabel = lado === 'frente' ? 'Frente' : 'Dorso';
  const previewTitle = `${garanteNombre} · DNI ${sideLabel}`;

  return (
    <button
      type="button"
      onClick={() => onOpenPreview({ documento: toDocumentoRecord(file), title: previewTitle })}
      className={cn(
        'group overflow-hidden rounded-lg border border-gray-200 bg-white text-left transition hover:border-gray-300 hover:shadow-sm',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2'
      )}
    >
      <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">DNI {sideLabel}</p>
          <p className="text-[11px] text-gray-400">{file.formattedSize}</p>
        </div>
        <Eye className="h-4 w-4 text-gray-400 transition group-hover:text-gray-700" />
      </div>

      <div className="relative flex h-28 items-center justify-center bg-gray-100">
        {isLoading && <Skeleton className="h-full w-full rounded-none" />}

        {!isLoading && isImage && previewUrl && (
          <img
            src={previewUrl}
            alt={`Miniatura de DNI ${sideLabel} de ${garanteNombre}`}
            className="h-full w-full object-cover"
          />
        )}

        {!isLoading && (!isImage || !previewUrl || hasPreviewError) && (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-3 text-center">
            {file.mimeType === 'application/pdf' ? (
              <FileText className="h-8 w-8 text-gray-500" />
            ) : (
              <ImageIcon className="h-8 w-8 text-gray-500" />
            )}
            <p className="line-clamp-2 text-xs text-gray-600">{file.fileName}</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between px-3 py-2">
        <span className="truncate text-xs text-gray-500">{file.fileName}</span>
        <span className="rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 transition group-hover:bg-gray-200">
          Ver
        </span>
      </div>
    </button>
  );
};

const useGaranteThumbnail = (file: GaranteFileRecord) => {
  const isImage = file.mimeType.startsWith(IMAGE_MIME_PREFIX);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(isImage);
  const [hasPreviewError, setHasPreviewError] = React.useState(false);

  React.useEffect(() => {
    if (!isImage || !file.fileUrl) {
      setPreviewUrl(null);
      setIsLoading(false);
      setHasPreviewError(false);
      return;
    }

    let isMounted = true;
    let currentObjectUrl: string | null = null;

    const loadThumbnail = async () => {
      setIsLoading(true);
      setHasPreviewError(false);

      try {
        const response = await PasseriniAPI.get(file.fileUrl.replace(/^\/api/, ''), {
          responseType: 'blob',
        });

        if (!isMounted) return;

        currentObjectUrl = URL.createObjectURL(response.data);
        setPreviewUrl(currentObjectUrl);
      } catch (error) {
        console.error('Error fetching garante DNI thumbnail:', error);
        if (isMounted) {
          setPreviewUrl(null);
          setHasPreviewError(true);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadThumbnail();

    return () => {
      isMounted = false;
      if (currentObjectUrl) {
        URL.revokeObjectURL(currentObjectUrl);
      }
    };
  }, [file.fileUrl, isImage]);

  return { previewUrl, isLoading, hasPreviewError, isImage };
};
